<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1120"
    persistent
    class="api-test-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card api-test-dialog__card">
      <div class="dialog-header api-test-dialog__header d-flex align-center justify-space-between">
        <div class="d-flex align-center ga-2 min-w-0">
          <v-icon color="primary" size="20">mdi-flask-outline</v-icon>
          <div class="min-w-0">
            <span class="dialog-header__title d-block text-truncate">Test API</span>
            <span class="api-test-dialog__subtitle text-truncate d-block">
              {{ endpoint?.description || endpoint?.path }}
            </span>
          </div>
        </div>
        <v-btn
          icon
          variant="text"
          size="small"
          class="dialog-close-btn"
          :disabled="loading"
          rounded="lg"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body api-test-dialog__body">
        <div class="api-test-dialog__meta">
          <span class="api-test-dialog__method">{{ endpoint?.method || 'PUT' }}</span>
          <code class="api-test-dialog__url">{{ requestUrl }}</code>
          <span class="api-test-dialog__lang">{{ languageLabel }}</span>
        </div>

        <div class="api-test-dialog__api-key">
          <span class="api-test-dialog__label mb-0">API key</span>
          <v-text-field
            :model-value="maskedApiKey"
            variant="outlined"
            density="compact"
            hide-details
            readonly
            rounded="lg"
            class="api-test-dialog__api-key-field"
          >
            <template #prepend-inner>
              <v-icon size="16" color="primary">mdi-key-outline</v-icon>
            </template>
          </v-text-field>
        </div>

        <!-- task_id is a UUID nobody can be expected to know by heart, and it
             was previously only obtainable by digging through the portal. This
             picker lists the caller's own campaigns and writes the chosen id
             straight into the request body below. Only rendered for endpoints
             that actually take a task_id. -->
        <div v-if="needsTaskId" class="api-test-dialog__campaign">
          <span class="api-test-dialog__label mb-0">Campaign</span>
          <v-select
            v-model="selectedTaskId"
            :items="campaignOptions"
            item-title="label"
            item-value="value"
            :loading="loadingCampaigns"
            :disabled="loading || loadingCampaigns"
            :hint="campaignHint"
            persistent-hint
            variant="outlined"
            density="compact"
            rounded="lg"
            placeholder="Select a campaign to fill task_id"
            class="api-test-dialog__campaign-field"
            @update:model-value="applyTaskId"
          >
            <template #prepend-inner>
              <v-icon size="16" color="primary">mdi-bullhorn-outline</v-icon>
            </template>
          </v-select>
        </div>

        <div class="api-test-dialog__split">
          <div class="api-test-dialog__pane api-test-dialog__pane--request">
            <div class="api-test-dialog__pane-head">
              <label class="api-test-dialog__label mb-0" for="api-test-body">
                Request body (JSON)
              </label>
            </div>
            <div class="api-test-dialog__editor-wrap">
              <textarea
                id="api-test-body"
                v-model="requestBodyText"
                class="api-test-dialog__editor"
                spellcheck="false"
                autocapitalize="off"
                autocomplete="off"
                @input="onBodyInput"
              />
            </div>
            <p v-if="parseError" class="api-test-dialog__error mb-0">
              {{ parseError }}
            </p>
          </div>

          <div class="api-test-dialog__pane api-test-dialog__pane--response">
            <div class="api-test-dialog__pane-head api-test-dialog__response-head">
              <span class="api-test-dialog__label mb-0">Response</span>
              <template v-if="result">
                <span
                  class="api-test-dialog__api-status"
                  :class="responseOk ? 'api-test-dialog__tone--ok' : 'api-test-dialog__tone--err'"
                >
                  {{ responseStatus || (responseOk ? 'SUCCESS' : 'ERROR') }}
                </span>
                <span class="api-test-dialog__elapsed">{{ result.elapsedMs }} ms</span>
              </template>
            </div>
            <div
              class="api-test-dialog__response-body"
              :class="{ 'api-test-dialog__response-body--error': hasResponseOutput && !responseOk }"
            >
              <p v-if="responseMessage" class="api-test-dialog__message">
                {{ responseMessage }}
              </p>
              <pre v-if="highlightedResponse"><code v-html="highlightedResponse" /></pre>
              <p
                v-else-if="!hasResponseOutput"
                class="api-test-dialog__response-empty mb-0"
              >
                Send a request to view the response here.
              </p>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="dialog-footer api-test-dialog__footer">
        <v-btn
          variant="outlined"
          color="primary"
          size="small"
          class="text-none api-test-dialog__reset-btn"
          rounded="lg"
          :disabled="loading"
          @click="resetBody"
        >
          Reset
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          size="small"
          class="text-none btn-primary-cta"
          rounded="lg"
          prepend-icon="mdi-play"
          :loading="loading"
          :disabled="Boolean(parseError) || !requestBodyText.trim() || !hasApiKey"
          @click="sendRequest"
        >
          Send request
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  buildEditableTestRequestBody,
  highlightJson,
} from '@/utils/apiDocsHighlight'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { maskApiKey } from '@/utils/apiKeys'
import { runPublicApiTest } from '@/api/campaignsApi'

export default {
  name: 'ApiDocsTestDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    endpoint: {
      type: Object,
      default: null,
    },
    baseUrl: {
      type: String,
      required: true,
    },
    apiKey: {
      type: String,
      default: '',
    },
    language: {
      type: String,
      default: 'node',
      validator: (value) => ['node', 'python'].includes(value),
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      requestBodyText: '',
      defaultBodyText: '',
      parseError: '',
      inlineError: '',
      loading: false,
      result: null,
      campaigns: [],
      loadingCampaigns: false,
      campaignsError: '',
      selectedTaskId: '',
    }
  },
  computed: {
    // Driven by the endpoint's own default body rather than a hardcoded path,
    // so any future endpoint that takes a task_id gets the picker for free.
    needsTaskId() {
      if (!this.defaultBodyText) return false
      try {
        return Object.prototype.hasOwnProperty.call(JSON.parse(this.defaultBodyText), 'task_id')
      } catch {
        return false
      }
    },
    campaignOptions() {
      return this.campaigns.map((c) => ({
        value: c.id,
        // The id is in the label on purpose: it is what ends up in the body,
        // and seeing it here is what connects the two for someone reading the
        // JSON underneath.
        label: `${c.name}${c.callable === false ? ' — not callable' : ''} · ${c.id}`,
        disabled: c.callable === false,
      }))
    },
    campaignHint() {
      if (this.loadingCampaigns) return 'Loading your campaigns…'
      if (this.campaignsError) return this.campaignsError
      if (!this.campaigns.length) return 'No outbound campaigns found — create one on the Campaigns page.'
      return 'Writes the selected campaign’s id into task_id below.'
    },
    requestUrl() {
      const root = String(this.baseUrl ?? '').replace(/\/$/, '')
      const path = this.endpoint?.path ?? ''
      return `${root}${path}`
    },
    languageLabel() {
      return this.language === 'python' ? 'Python' : 'Node.js'
    },
    hasApiKey() {
      return Boolean(String(this.apiKey ?? '').trim())
    },
    maskedApiKey() {
      return maskApiKey(this.apiKey)
    },
    hasResponseOutput() {
      return Boolean(this.result || this.inlineError)
    },
    responseBody() {
      if (!this.result || typeof this.result.body !== 'object' || this.result.body === null) {
        return null
      }
      return this.result.body
    },
    responseStatus() {
      const status = this.responseBody?.status ?? this.responseBody?.Status
      return status ? String(status).trim().toUpperCase() : ''
    },
    responseOk() {
      if (this.inlineError) return false
      if (this.responseBody) return isApiSuccess(this.responseBody)
      return Boolean(this.result?.ok)
    },
    responseMessage() {
      if (this.inlineError) return this.inlineError
      return this.getResponseMessage(this.result?.body, '')
    },
    highlightedResponse() {
      if (this.inlineError && !this.result) return ''
      if (!this.result) return ''

      const payload =
        typeof this.result.body === 'string'
          ? this.result.body
          : JSON.stringify(this.result.body, null, 2)
      return highlightJson(payload)
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.initialize()
    },
    endpoint() {
      if (this.modelValue) this.initialize()
    },
  },
  methods: {
    getResponseMessage(body, fallback = 'Request completed.') {
      if (body && typeof body === 'object') {
        return (
          body.status_message ||
          body.statusMessage ||
          getApiMessage(body, fallback)
        )
      }
      if (typeof body === 'string' && body.trim()) {
        return body.trim()
      }
      return fallback
    },
    initialize() {
      this.result = null
      this.inlineError = ''
      this.defaultBodyText = buildEditableTestRequestBody(
        this.endpoint?.requestBody ?? '{}',
      )
      this.requestBodyText = this.defaultBodyText
      this.selectedTaskId = ''
      this.validateBody()
      if (this.needsTaskId) this.loadCampaigns()
    },
    async loadCampaigns() {
      // Loaded lazily, only for endpoints that need a task_id, so opening any
      // other endpoint's tester costs nothing.
      if (this.loadingCampaigns) return
      this.loadingCampaigns = true
      this.campaignsError = ''
      try {
        // Fetched through the API's own /campaigns endpoint rather than the
        // portal's campaign list, so `callable` is the server's answer instead
        // of a copy of its rules. That distinction matters: eligibility turns
        // on the campaign having call configuration, NOT on it being an AI
        // campaign -- an older human-agent campaign can carry an OUTGOING_CALL
        // action and dials perfectly well, while the auto-provisioned default
        // campaign has no configuration and cannot.
        const response = await runPublicApiTest({ path: '/campaigns', method: 'GET' })
        if (response.status === 'ERROR' || response.data?.http_status !== 200) {
          this.campaignsError = response.message
            || response.data?.body?.error
            || 'Could not load campaigns.'
          this.campaigns = []
          return
        }
        this.campaigns = (response.data.body?.data?.items ?? [])
          .map((c) => ({ id: c.task_id, name: c.task_name, callable: c.callable }))
          .filter((c) => c.id)
      } catch (error) {
        this.campaignsError = getApiMessage(error, 'Could not load campaigns.')
        this.campaigns = []
      } finally {
        this.loadingCampaigns = false
      }
    },
    applyTaskId(taskId) {
      // Rewrites just the task_id key and leaves the rest of the body (and the
      // user's own edits to it) untouched.
      if (!taskId) return
      let parsed
      try {
        parsed = JSON.parse(String(this.requestBodyText ?? '').trim() || '{}')
      } catch {
        // Body is mid-edit and not valid JSON -- don't clobber what they typed.
        this.inlineError = 'Fix the JSON below before selecting a campaign.'
        return
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return
      parsed.task_id = taskId
      this.requestBodyText = JSON.stringify(parsed, null, 2)
      this.result = null
      this.inlineError = ''
      this.validateBody()
    },
    onBodyInput() {
      this.result = null
      this.inlineError = ''
      this.validateBody()
    },
    validateBody() {
      const text = String(this.requestBodyText ?? '').trim()
      if (!text) {
        this.parseError = 'Request body cannot be empty.'
        return false
      }

      try {
        const parsed = JSON.parse(text)
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          this.parseError = 'Request body must be a JSON object.'
          return false
        }
        this.parseError = ''
        return true
      } catch (err) {
        this.parseError = `Invalid JSON: ${err.message}`
        return false
      }
    },
    resetBody() {
      this.requestBodyText = this.defaultBodyText
      this.result = null
      this.inlineError = ''
      this.validateBody()
    },
    close() {
      if (this.loading) return
      this.$emit('update:modelValue', false)
    },
    async sendRequest() {
      if (!this.validateBody()) return
      this.loading = true
      this.inlineError = ''
      this.result = null

      try {
        // Routed through the portal's backend, not sent from here. Signing a
        // request needs the organisation's OAuth secret, and a browser cannot
        // hold one -- anyone with developer tools would have credentials that
        // place real, billed calls.
        const proxied = await runPublicApiTest({
          path: this.endpoint?.path,
          method: this.endpoint?.method,
          body: JSON.parse(this.requestBodyText),
        })

        if (proxied.status === 'ERROR') {
          this.inlineError = proxied.message || 'Request failed.'
          this.result = null
        } else {
          const result = {
            ok: proxied.data.http_status >= 200 && proxied.data.http_status < 300,
            status: proxied.data.http_status,
            statusText: '',
            elapsedMs: proxied.data.elapsed_ms,
            body: proxied.data.body,
            raw: typeof proxied.data.body === 'string'
              ? proxied.data.body
              : JSON.stringify(proxied.data.body, null, 2),
          }
          this.result = result
          if (!isApiSuccess(result.body)) {
            this.inlineError =
              this.getResponseMessage(result.body, `HTTP ${result.status}`) ||
              'Request failed.'
          }
        }
      } catch (err) {
        this.inlineError =
          err?.message ||
          'Request failed. Check your network connection and CORS settings.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.api-test-dialog__card {
  display: flex;
  flex-direction: column;
  max-height: min(92vh, 840px);
  overflow: hidden;
}

.api-test-dialog__header {
  flex-shrink: 0;
  padding-top: var(--y-space-2);
  padding-bottom: var(--y-space-2);
}

.api-test-dialog__subtitle {
  margin-top: 1px;
  font-size: 0.6875rem;
  color: var(--y-text-muted);
}

.api-test-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
  overflow: hidden;
}

.api-test-dialog__meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.5rem 0.625rem;
  border-radius: 8px;
  border: 1px solid var(--y-glass-border);
  background: var(--y-panel-bg);
}

.api-test-dialog__method {
  flex-shrink: 0;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #0c447c;
  background: #e6f1fb;
}

.api-test-dialog__url {
  flex: 1 1 auto;
  min-width: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.6875rem;
  line-height: 1.4;
  color: var(--y-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.api-test-dialog__lang {
  flex-shrink: 0;
  font-size: 0.625rem;
  font-weight: 600;
  color: var(--y-code-panel-muted);
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--y-glass-surface);
  border: 1px solid var(--y-glass-border);
}

.api-test-dialog__api-key {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.api-test-dialog__campaign {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

/* The id is part of the option label, so it needs to stay readable at the
   same size as the JSON body it gets written into. */
.api-test-dialog__campaign-field :deep(.v-field) {
  background: var(--y-panel-bg) !important;
}

.api-test-dialog__campaign-field :deep(.v-select__selection-text) {
  font-size: 0.8125rem;
}

.api-test-dialog__api-key-field :deep(.v-field) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  background: var(--y-panel-bg) !important;
}

.api-test-dialog__api-key-field :deep(.v-field__input) {
  cursor: default;
}

.api-test-dialog__label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--y-text-muted);
}

.api-test-dialog__split {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  gap: 1rem;
  align-items: stretch;
}

.api-test-dialog__pane {
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow: hidden;
}

.api-test-dialog__pane-head {
  display: flex;
  align-items: center;
  min-height: 1.5rem;
  flex-shrink: 0;
}

.api-test-dialog__pane--response {
  border-left: 1px solid var(--y-glass-border);
  padding-left: 1rem;
}

.api-test-dialog__editor-wrap {
  flex: 1 1 auto;
  min-height: 0;
  border-radius: 10px;
  border: 1px solid var(--y-code-panel-border);
  background: var(--y-code-panel-bg);
  overflow: hidden;
}

.api-test-dialog__response-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  border-radius: 10px;
  border: 1px solid var(--y-code-panel-border);
  background: var(--y-code-panel-bg);
  padding: 0.75rem 0.875rem;
}

.api-test-dialog__response-empty {
  min-height: 12rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1rem;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--y-code-panel-muted);
}

.api-test-dialog__editor {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0.875rem 1rem;
  border: 0;
  outline: none;
  resize: none;
  overflow-y: auto;
  overflow-x: hidden;
  background: transparent;
  color: var(--y-code-panel-text);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  tab-size: 2;
  box-sizing: border-box;
  caret-color: var(--y-primary, #6366f1);
}

.api-test-dialog__response-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  width: 100%;
}

.api-test-dialog__response-body--error {
  border-color: rgba(239, 68, 68, 0.3);
}

.api-test-dialog__message {
  margin: 0 0 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--y-text-secondary);
}

.api-test-dialog__response-body pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: visible;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--y-code-panel-text);
}

.api-test-dialog__response-body pre code {
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
}
.api-test-dialog__api-status {
  margin-left: auto;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.api-test-dialog__tone--ok {
  color: rgb(134, 239, 172);
}

.api-test-dialog__api-status.api-test-dialog__tone--ok {
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.25);
}

.api-test-dialog__tone--err {
  color: rgb(252, 165, 165);
}

.api-test-dialog__api-status.api-test-dialog__tone--err {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.25);
}

.api-test-dialog__elapsed {
  font-size: 0.6875rem;
  color: var(--y-text-muted);
  font-variant-numeric: tabular-nums;
}

.api-test-dialog__response-body :deep(.green) {
  color: #6db33f;
}

.api-test-dialog__response-body :deep(.gray) {
  color: var(--y-text-muted);
}

.api-test-dialog__response-body :deep(.str) {
  color: #5eb3f6;
}

.api-test-dialog__response-body :deep(.kw) {
  color: #e08a71;
}

.api-test-dialog__error {
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgb(252, 165, 165);
}

.api-test-dialog__footer {
  flex-shrink: 0;
  padding-top: 0.5rem !important;
  padding-bottom: 0.75rem !important;
}

.api-test-dialog__reset-btn {
  font-weight: 600 !important;
  border-color: rgba(99, 102, 241, 0.35) !important;
  color: #4338ca !important;
}
</style>
